"use client";

import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { MenuItem } from "@/types/menu";
import { FiChevronDown, FiChevronRight, FiMenu, FiX } from "react-icons/fi";
import { menuData } from "@/data/menuData";
import CallNowButtonVisit from "../callNowButtonVisit";
import useMilesOneDeepLink from "@/hooks/useMilesOneDeepLink";

interface MobileAccordionProps {
  node: MenuItem;
  depth?: number;
  activeChain: MenuItem[];
  pathname: string;
  idPath?: string;
  onDownloadAppClick?: () => void;
}
const LOGO_SRC =
  "https://ik.imagekit.io/mileseducation/miles_website/home_new/miles-logo-new.svg?tr=w-170,h-50";

const DownloadApp = () => (
  <div className="flex flex-row gap-4 justify-center">
    <div className="flex flex-col justify-center gap-3">
      <Link
        href="https://apps.apple.com/us/app/miles-us-pathway/id6504799221"
        target="_blank"
        className="bg-[#061A2F] p-2 flex rounded-lg gap-2 pr-4 items-center"
      >
        <Image
          src="https://ik.imagekit.io/mileseducation/miles_website/home/app-store.webp?tr=w-48,h-49,q-80"
          alt="App Store"
          width={40}
          height={41}
          className="shrink-0"
        />
        <div>
          <p className="text-[10px] leading-3 text-white font-medium">
            Download on the
          </p>
          <p className="text-base text-white font-medium">App Store</p>
        </div>
      </Link>
      <Link
        href="https://play.google.com/store/apps/details?id=com.miles.one"
        target="_blank"
        className="bg-[#061A2F] p-2 flex rounded-lg gap-2 pr-4 items-center"
      >
        <Image
          src="https://ik.imagekit.io/mileseducation/miles_website/home/play-store.webp?tr=w-48,h-49,q-80"
          alt="Play Store"
          width={40}
          height={41}
          className="shrink-0"
        />
        <div>
          <p className="text-[10px] leading-3 text-white font-medium">
            GET IT ON
          </p>
          <p className="text-base text-white font-medium">Google Play</p>
        </div>
      </Link>
    </div>
    <div className="text-center">
      <Image
        src="https://ik.imagekit.io/mileseducation/miles_website/home/milesone_qr_code.webp"
        alt="QR Code"
        width={100}
        height={100}
        className="min-w-[100px] min-h-[100px] inline-block"
      />
      <p className="pt-1 text-xs text-[#fff]">Scan to download</p>
    </div>
  </div>
);

function classNames(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}

const isExternal = (url: string) => /^https?:\/\//i.test(url);
const getChildren = (item: any): MenuItem[] | undefined =>
  item?.subItems || item?.subNav;

const matchesPath = (itemUrl: string | undefined, pathname: string) => {
  if (!itemUrl || itemUrl === "") return false;
  if (itemUrl === "/") return pathname === "/";
  if (pathname === itemUrl) return true;
  if (pathname.startsWith(itemUrl + "/")) return true;
  return false;
};

const findActiveChain = (items: MenuItem[], pathname: string): MenuItem[] => {
  let best: MenuItem[] = [];
  for (const item of items) {
    const children = getChildren(item) || [];
    const childChain = children.length
      ? findActiveChain(children, pathname)
      : [];
    const selfMatches = matchesPath(item.url, pathname);

    let currentChain: MenuItem[] = [];
    if (childChain.length || selfMatches) {
      currentChain = new Array(1 + childChain.length);
      currentChain[0] = item;
      for (let i = 0; i < childChain.length; i++) {
        currentChain[i + 1] = childChain[i];
      }
    }
    if (currentChain.length > best.length) best = currentChain;
  }
  return best;
};

// Shared by both breakpoints: the desktop dropdown renders it directly, and the
// mobile drawer picks it up through `mobileMenuItems` below, so the labels and
// their order can only be changed in one place.
const accountingFinancePrograms = [
  { title: "CAIRA – AI-Ready Accountant", url: "/caira" },
  { title: "U.S. CPA (Certified Public Accountant)", url: "/cpa" },
  { title: "U.S. CMA (Certified Management Accountant)", url: "/cma" },
  { title: "U.S. EA (Enrolled Agent)", url: "/ea" },
  { title: "US Pathway for Accountants", url: "/us-jobs-for-accountants" },
  { title: "Accounting Jobs", url: "/cpa-cma-job-opportunities-india" },
  { title: "Miles Alumni", url: "/cpa-cma-career-success-stories-india" },
  { title: "Blog", url: "/blog" },
];

const ACCOUNTING_TITLE = "Accounting & Finance Programs";

/**
 * The mobile drawer's top level, matching the desktop bar: programs, About,
 * Miles Ecosystem.
 *
 * Derived from menuData rather than replacing it — the CPA and CMA sub-page
 * trees still live there, and two other headers still read it, so only the
 * programs branch is swapped for the desktop list (same labels, same order)
 * with the nested pages carried across by url.
 */
const mobileMenuItems: MenuItem[] = menuData.flatMap((node) => {
  if (node.title !== ACCOUNTING_TITLE) return [node];

  const nestedByUrl = new Map(
    (getChildren(node) || [])
      .filter((child) => (getChildren(child) || []).length > 0)
      .map((child) => [child.url, getChildren(child) as MenuItem[]]),
  );

  return [
    {
      ...node,
      // No url, so this row is a pure accordion toggle: item 1 is not
      // clickable on either breakpoint, and MobileAccordion's synthetic
      // "… Home" link to /accounting drops out with it.
      url: "",
      subNav: accountingFinancePrograms.map((item) => {
        const subItems = nestedByUrl.get(item.url);
        return subItems ? { ...item, subItems } : { ...item };
      }),
    },
    { title: "About", url: "/about-us" },
  ];
});

const milesEcosystemItems = [
  {
    id: 1,
    title: "Miles Masterclass",
    url: "https://www.milesmasterclass.com/accounting/home",
    icon: "https://ik.imagekit.io/mileseducation/miles_website/home/ecosystem/masterclass-icon.png",
  },
  {
    id: 2,
    title: "Miles Talent Hub",
    url: "https://www.milestalenthub.com/",
    icon: "https://ik.imagekit.io/mileseducation/miles_website/home/ecosystem/talent-hub-icon.png",
  },
  // {
  //   id: 3,
  //   title: "Miles Nursing Hub",
  //   url: "https://milesnursinghub.com/",
  //   icon: "https://ik.imagekit.io/mileseducation/miles_website/home/ecosystem/nursing-icon.png",
  // },
  {
    id: 3,
    title: "Miles Campus",
    url: "https://www.milescampus.ai/",
    icon: "https://ik.imagekit.io/mileseducation/miles_website/home/ecosystem/campus-icon.png",
  },
  {
    id: 4,
    title: "Miles Global Education",
    url: "/certifications",
    icon: "https://ik.imagekit.io/mileseducation/miles_website/home/ecosystem/global-edu-icon.png",
  },
  {
    id: 5,
    title: "Futurense Technologies",
    url: "https://futurense.com/",
    icon: "https://ik.imagekit.io/mileseducation/miles_website/home/ecosystem/futurense-icon.png",
  },
  {
    id: 6,
    title: "Mojo Campus",
    url: "https://www.mojocampus.com/",
    icon: "https://ik.imagekit.io/mileseducation/miles_website/home/ecosystem/mojo-icon.png",
  },
  {
    id: 7,
    title: "Miles Foundation",
    url: "/miles-foundation",
    icon: "https://ik.imagekit.io/mileseducation/miles_website/home/ecosystem/foundation-icon.png",
  },
  {
    id: 8,
    title: "Miles MBBS | Doctors",
    url: "/mbbs",
    icon: "https://ik.imagekit.io/mileseducation/miles_website/home/ecosystem/foundation-icon.png",
  },
];

// New Accounting & Finance Programs Dropdown
const AccountingFinanceDropdown: React.FC<{
  pathname: string;
}> = ({ pathname }) => {
  return (
    <div
      className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition
                 duration-150 ease-out bottom-0 h-full absolute left-1/2 -translate-x-1/2 top-full z-50"
    >
      <div className="rounded-lg mt-3 bg-[#292929] text-white shadow-2xl ring-1 ring-black/5 overflow-hidden min-w-[280px]">
        {accountingFinancePrograms?.map((item, i) => (
          <Link
            key={i}
            href={item.url}
            target={isExternal(item.url) ? "_blank" : undefined}
            rel={isExternal(item.url) ? "noopener noreferrer" : undefined}
            className={classNames(
              "flex items-center gap-3 whitespace-pre px-5 py-2 hover:bg-white/10 border-b border-white/10 last:border-0",
              i === 0 ? "pt-3" : "",
              i === accountingFinancePrograms.length - 1 ? "pb-4" : "",
              matchesPath(item.url, pathname) && "bg-white/10 font-semibold",
            )}
          >
            <span
              className={classNames(
                "text-[14px] leading-6 font-normal",
                matchesPath(item.url, pathname) &&
                  "bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] bg-clip-text text-transparent font-semibold",
              )}
            >
              {item.title}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

// New Miles Ecosystem Dropdown with visual layout matching the screenshot
const MilesEcosystemDropdown: React.FC<{ pathname: string }> = ({
  pathname,
}) => {
  return (
    <div
      className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition
                 duration-150 ease-out bottom-0 h-full absolute left-1/2 -translate-x-1/2 top-full z-50"
    >
      <div className="rounded-[24px] mt-3 bg-white shadow-2xl overflow-hidden w-[835px] max-w-[95vw] p-4">
        {/* Header with gradient background and image */}
        <div className="relative px-8 py-6 overflow-hidden rounded-[8px]">
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/home_new/drodown_heading_bg.webp"
            alt="Miles Ecosystem Background"
            fill
            className="object-cover absolute inset-0"
            style={{ objectPosition: "center" }}
          />
          <h3 className="relative z-10 text-[32px] font-bold text-white tracking-wide">
            MILES ECOSYSTEM
          </h3>
        </div>

        {/* Content area */}
        <div className="bg-white px-4 pt-6 pb-3">
          <div className="grid grid-cols-3 gap-x-8 gap-y-2">
            {milesEcosystemItems?.map((item) => {
              const hasValidUrl = item.url && item.url !== "#";
              const isActive = matchesPath(item.url, pathname);
              const content = (
                <>
                  <span className="bg-[linear-gradient(146deg,#3399FF_14.91%,#FFBA08_83.78%)] bg-clip-text text-transparent shrink-0 text-[24px] font-bold w-6">
                    {item.id}.
                  </span>
                  <span
                    className={classNames(
                      "text-[14px] font-normal transition-colors",
                      isActive
                        ? "bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] bg-clip-text text-transparent font-semibold"
                        : "text-black",
                    )}
                  >
                    {item.title}
                  </span>
                </>
              );

              return hasValidUrl ? (
                <Link
                  key={item.id}
                  href={item.url}
                  target={isExternal(item.url) ? "_blank" : undefined}
                  rel={isExternal(item.url) ? "noopener noreferrer" : undefined}
                  className="flex items-center gap-3 group/item"
                >
                  {content}
                </Link>
              ) : (
                <div key={item.id} className="flex items-center gap-3">
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const depthPadding = ["pl-3", "pl-4", "pl-8"];
const MobileAccordion: React.FC<MobileAccordionProps> = React.memo(
  ({
    node,
    depth = 0,
    activeChain,
    pathname,
    idPath = "root",
    onDownloadAppClick,
  }) => {
    let children = getChildren(node);
    const hasChildren = (children?.length || 0) > 0;
    if (hasChildren && node.url) {
      const homeLabel = /cpa$/i.test(node.url || "")
        ? "CPA Home"
        : /cma$/i.test(node.url || "")
          ? "CMA Home"
          : `${node.title} Home`;
      const synthetic = {
        title: homeLabel,
        url: node.url,
        __syntheticHome: true,
      } as any;
      if (!children!.some((c: any) => c.__syntheticHome)) {
        children = [synthetic, ...children!];
      }
    }
    const padClass = depthPadding[Math.min(depth, depthPadding.length - 1)];
    const isNestedParent = depth > 0 && hasChildren;
    const isActive =
      node.url === "/"
        ? pathname === "/"
        : (node as any).__syntheticHome
          ? pathname === node.url
          : activeChain.includes(node) || matchesPath(node.url, pathname);
    const descendantActive = (() => {
      if (!children) return false;
      const stack = [...children];
      while (stack.length) {
        const c: any = stack.pop();
        if (activeChain.includes(c) || matchesPath(c.url, pathname))
          return true;
        const gc = getChildren(c);
        if (gc && gc.length) stack.push(...gc);
      }
      return false;
    })();
    const [open, setOpen] = useState(isActive || descendantActive);

    useEffect(() => {
      const handler = (e: any) => {
        const { targetIdPath, depth: evtDepth } = e.detail || {};
        if (evtDepth === depth && targetIdPath !== idPath) {
          setOpen(false);
        }
      };
      window.addEventListener("mobile-accordion-open", handler as any);
      return () =>
        window.removeEventListener("mobile-accordion-open", handler as any);
    }, [depth, idPath]);

    const toggleOpen = () => {
      const next = !open;
      setOpen(next);
      if (next) {
        const evt = new CustomEvent("mobile-accordion-open", {
          detail: { targetIdPath: idPath, depth },
        });
        window.dispatchEvent(evt);
      }
    };

    return (
      <div className="border-b border-white/10 last:border-0">
        <div
          className={classNames(
            "flex items-center justify-between py-2 px-4 text-sm border-b border-white/10 last:border-0",
            padClass,
            isNestedParent && "gap-2",
          )}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={toggleOpen}
              className={classNames(
                "flex-1 text-left focus:outline-none rounded transition-colors ",
                isActive
                  ? "bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] bg-clip-text text-transparent font-semibold"
                  : "text-white ",
              )}
              aria-expanded={(open ? "true" : "false") as "true" | "false"}
              aria-label={open ? "Collapse" : "Expand"}
            >
              {node.title}
            </button>
          ) : node.url ? (
            node.title === "Download App" ? (
              <button
                type="button"
                onClick={onDownloadAppClick}
                className={classNames(
                  "flex-1 text-left focus:outline-none rounded transition-colors ",
                  "text-white",
                )}
              >
                {node.title}
              </button>
            ) : (
              <Link
                href={node.url}
                className={classNames(
                  "flex-1 text-left focus:outline-none rounded transition-colors",
                  isActive
                    ? "bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] bg-clip-text text-transparent font-semibold"
                    : "text-white",
                )}
                target={
                  isExternal(node.url) || node.title === "Login"
                    ? "_blank"
                    : undefined
                }
                rel={
                  isExternal(node.url) || node.title === "Login"
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                {node.title}
              </Link>
            )
          ) : (
            <span className="flex-1 text-left text-white">{node.title}</span>
          )}
          {hasChildren && (
            <button
              aria-label={open ? "Collapse" : "Expand"}
              aria-expanded={(open ? "true" : "false") as "true" | "false"}
              className={classNames(
                "p-1 w-6 flex justify-center transition-colors",
                isActive ? "text-[#f5b644]" : "text-white",
              )}
              onClick={toggleOpen}
              type="button"
            >
              <FiChevronRight
                className={classNames("transition", open && "rotate-90")}
              />
            </button>
          )}
        </div>
        {hasChildren && (
          <div
            className={classNames(
              "overflow-hidden transition-[max-height] duration-300",
              open ? "max-h-[1000px]" : "max-h-0",
            )}
          >
            <div className="pb-2">
              {children?.map((child, i) => {
                const childIdPath =
                  idPath + "/" + (child.url || child.title || i);
                return (
                  <div
                    key={i}
                    className="pl-3 border-b border-white/10 last:border-0"
                  >
                    <MobileAccordion
                      node={child}
                      depth={depth + 1}
                      activeChain={activeChain}
                      pathname={pathname}
                      idPath={childIdPath}
                      onDownloadAppClick={onDownloadAppClick}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  },
);

// Exclude specific pages from showing login button
const EXCLUDED_LOGIN_PATHS = new Set([
  "/cpa-cma-career-success-stories-india",
  "/cpa-cma-job-opportunities-india",
]);

const isPathExcluded = (path: string | null): boolean => {
  if (!path) return false;
  if (EXCLUDED_LOGIN_PATHS.has(path)) return true;
  return Array.from(EXCLUDED_LOGIN_PATHS).some((p) => path.startsWith(p + "/"));
};

// `transparent`: header floats over the next section (e.g. a full-bleed hero)
// until it pins on scroll, then falls back to the solid background.
const GlobalHeaderUpdated: React.FC<{ callHref?: string; transparent?: boolean }> = ({
  transparent = false,
}) => {
  const pathname = usePathname();
  const activeChain = useMemo(() => {
    const raw = findActiveChain(menuData, pathname || "/");
    return pathname === "/" ? raw : raw.filter((m) => m.url !== "/");
  }, [pathname]);
  // The drawer needs its own chain: MobileAccordion marks a row active by
  // object identity, and the programs branch of mobileMenuItems is a copy, so
  // the menuData chain above would never match it. Kept separate because
  // `secondaryItems` below still matches menuData titles.
  const mobileActiveChain = useMemo(() => {
    const raw = findActiveChain(mobileMenuItems, pathname || "/");
    return pathname === "/" ? raw : raw.filter((m) => m.url !== "/");
  }, [pathname]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);

  // Deep link hook for mobile app redirect
  const { tryOpenApp, isAndroid, isIOS } = useMilesOneDeepLink();

  // CPA/CMA login button
  const isCpaPage = pathname?.startsWith("/cpa");
  const isCmaPage = pathname?.startsWith("/cma");
  const isMilesFoundationPage = pathname === "/miles-foundation";
  const isExcludedLoginPage = isPathExcluded(pathname);

  const showLoginButton = (isCpaPage || isCmaPage) && !isExcludedLoginPage;
  const loginUrl = isCpaPage
    ? "https://cpa.mileseducation.com/"
    : isCmaPage
      ? "https://cma.mileseducation.com/"
      : "/login";

  const secondaryItems = useMemo(() => {
    if (!pathname) return [];
    if (pathname === "/accounting") return [];
    if (activeChain.length === 2) {
      const [parent, leaf] = activeChain;
      const leafChildren = getChildren(leaf) || [];
      if (leafChildren.length === 0) {
        const parentChildren = getChildren(parent) || [];
        if (parentChildren.includes(leaf)) return [];
      }
    }

    const chainWithChildren = [...activeChain]
      .reverse()
      .find((c) => (getChildren(c) || []).length > 0);
    let candidate: MenuItem | undefined = chainWithChildren || activeChain[0];
    if (!candidate) {
      const seg = pathname.split("/").filter(Boolean)[0];
      if (seg) {
        candidate = menuData.find(
          (m) => m.url && m.url.split("/").filter(Boolean)[0] === seg,
        );
      }
    }
    if (!candidate) return [];
    const baseChildren = getChildren(candidate) || [];
    let children = baseChildren;
    if (children.some((c: any) => ["U.S. CPA", "U.S. CMA"].includes(c.title))) {
      const match = children.find(
        (c: any) => c.url && pathname.startsWith(c.url),
      );
      const preferred =
        match ||
        children.find((c: any) => ["U.S. CPA", "U.S. CMA"].includes(c.title));
      if (preferred) {
        const grandChildren = getChildren(preferred) || [];
        if (grandChildren.length) children = grandChildren;
      }
    }
    const candidateIndex = activeChain.indexOf(candidate);
    const hasNested = baseChildren.some(
      (c: any) => (getChildren(c) || []).length > 0,
    );
    if (candidateIndex === 0 && !hasNested) return [];
    const currentLeaf = activeChain[activeChain.length - 1];
    if (currentLeaf && (getChildren(currentLeaf) || []).length === 0) {
      const isCpaCmaDeep =
        currentLeaf.url?.startsWith("/cpa/") ||
        currentLeaf.url?.startsWith("/cma/");
      if (!isCpaCmaDeep && children === baseChildren) return [];
    }
    return children;
  }, [pathname, activeChain]);

  useEffect(() => {
    setMobileOpen(false);
    setShowMobileMenu(false);
  }, [pathname]);

  const openMobile = useCallback(() => {
    setShowMobileMenu(true);
    requestAnimationFrame(() => {
      setMobileOpen(true);
    });
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setTimeout(() => {
      setShowMobileMenu(false);
    }, 300); // Match the transition duration
  }, []);

  // Handle download app click - mobile redirects, desktop shows modal
  const handleDownloadAppClick = useCallback(() => {
    const isMobile = isAndroid || isIOS;
    if (isMobile) {
      // Mobile: Direct redirect to app store
      tryOpenApp();
    } else {
      // Desktop: Show modal with QR code
      setShowDownloadModal(true);
    }
  }, [isAndroid, isIOS, tryOpenApp]);

  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "0px"; // Prevent layout shift
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [showMobileMenu]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobile();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, closeMobile]);

  useEffect(() => {
    let cancelled = false;
    const setup = () => {
      if (cancelled) return;
      const sentinel = document.getElementById("header-top-sentinel");
      if (!sentinel) return;
      const obs = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          setPinned(!entry.isIntersecting);
        },
        { rootMargin: "0px", threshold: 0 },
      );
      obs.observe(sentinel);
      return () => obs.disconnect();
    };
    let cleanup: any;
    if (typeof (window as any).requestIdleCallback === "function") {
      const id = (window as any).requestIdleCallback(() => {
        cleanup = setup();
      });
      return () => {
        cancelled = true;
        (window as any).cancelIdleCallback?.(id);
        cleanup && cleanup();
      };
    } else {
      cleanup = setup();
      return () => {
        cancelled = true;
        cleanup && cleanup();
      };
    }
  }, []);

  useEffect(() => {
    // A transparent header never reserves space, so pinning must not add any either
    if (pinned && !transparent && headerRef.current && spacerRef.current) {
      spacerRef.current.style.height = headerRef.current.offsetHeight + "px";
    } else if (spacerRef.current) {
      spacerRef.current.style.height = "0px";
    }
  }, [pinned, transparent, secondaryItems.length]);

  return (
    <>
      <div id="header-top-sentinel" aria-hidden="true" className="h-0" />
      <header
        ref={headerRef as any}
        className={classNames(
          "z-[999] transition-shadow",
          pinned
            ? "fixed top-0 left-0 right-0 shadow-lg shadow-black/30"
            : transparent
              ? "absolute left-0 right-0"
              : "relative",
        )}
      >
        <div
          className={classNames(
            "text-white",
            pinned
              ? "bg-[#191919]/95 backdrop-blur"
              : transparent
                ? "bg-transparent"
                : "bg-[#191919]",
          )}
        >
          <div className="mx-auto main_container relative py-2 lg:py-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link href="/" className="flex items-center gap-3">
                  <div
                    style={{
                      width: "120px",
                      height: "45px",
                      position: "relative",
                      contain: "layout style paint",
                    }}
                  >
                    <Image
                      src={LOGO_SRC}
                      alt="Miles Education"
                      width={133}
                      height={50}
                      priority={pathname === "/"}
                      sizes="120px"
                      className="dark:invert duration-300 object-contain"
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </Link>
              </div>

              <nav className="hidden lg:flex items-center gap-8 xl:gap-10 py-4">
                {/* Accounting & Finance Programs — a label, not a control: the
                    programs are the dropdown, so it no longer navigates to
                    /accounting. Hover still opens the panel, since that hangs
                    off `group` on this wrapper. */}
                <div className="relative group">
                  <div className="inline-flex items-center gap-2 py-2 cursor-default select-none">
                    <span
                      className={classNames(
                        "text-[15px]",
                        accountingFinancePrograms.some((item) =>
                          matchesPath(item.url, pathname || "/"),
                        ) || pathname === "/accounting"
                          ? "bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] bg-clip-text text-transparent font-semibold"
                          : "text-white font-normal",
                      )}
                    >
                      Accounting & Finance Programs
                    </span>
                    <FiChevronDown
                      className={classNames(
                        "w-4 h-4",
                        accountingFinancePrograms.some((item) =>
                          matchesPath(item.url, pathname || "/"),
                        ) || pathname === "/accounting"
                          ? "text-[#f5b644]"
                          : "text-white",
                      )}
                    />
                  </div>
                  <AccountingFinanceDropdown pathname={pathname || "/"} />
                </div>

                {/* About */}
                {/* <Link href="/about-us" className="py-2 focus:outline-none">
                  <span
                    className={classNames(
                      "text-[15px]",
                      matchesPath("/about-us", pathname || "/")
                        ? "bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] bg-clip-text text-transparent font-semibold"
                        : "text-white font-normal hover:text-white",
                    )}
                  >
                    About
                  </span>
                </Link> */}

                {/* Miles Ecosystem */}
                <div className="relative group">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 py-2 focus:outline-none rounded transition-colors group/btn"
                  >
                    <span
                      className={classNames(
                        "text-[15px]",
                        milesEcosystemItems.some((item) =>
                          matchesPath(item.url, pathname || "/"),
                        )
                          ? "bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] bg-clip-text text-transparent font-semibold"
                          : "text-white font-normal hover:text-white",
                        "group-focus/btn:bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] group-focus/btn:bg-clip-text group-focus/btn:text-transparent",
                      )}
                    >
                      Miles Ecosystem
                    </span>
                    <FiChevronDown
                      className={classNames(
                        "w-4 h-4",
                        milesEcosystemItems.some((item) =>
                          matchesPath(item.url, pathname || "/"),
                        )
                          ? "text-[#f5b644]"
                          : "text-white",
                        "group-focus/btn:text-[#f5b644]",
                      )}
                    />
                  </button>
                  <MilesEcosystemDropdown pathname={pathname || "/"} />
                </div>
              </nav>

              <div className="hidden lg:flex items-center gap-3">
                {/* Download App Button */}
                <button
                  type="button"
                  onClick={handleDownloadAppClick}
                  className="inline-flex gap-2 items-center justify-center rounded-[13px_13px_13px_0] px-4 py-2 border-2 border-[#3399FF] text-[#39f] text-[14px] font-bold min-w-[120px] h-[36px]"
                >
                  Download App
                </button>

                {/* Contact Us Button for Miles Foundation Page */}
                {isMilesFoundationPage ? (
                  <Link
                    href="/contact-us"
                    className="bg-[#3399FF] text-white rounded-[13px_13px_13px_0] flex items-center justify-center font-bold text-base gap-2 px-4 py-[5px] hover:brightness-95 transition-colors duration-200 h-[36px]"
                  >
                    <Image
                      src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/send.webp"
                      alt="Contact Us"
                      className="w-4 h-4"
                      width={16}
                      height={16}
                      priority
                    />
                    Contact Us
                  </Link>
                ) : (
                  <a
                    href="tel:+919513881313"
                    aria-label="Call us for more details"
                    className="bg-[#3399FF] text-white rounded-[13px_13px_13px_0] flex items-center justify-center font-bold text-base gap-2 px-2 py-[5px] pr-4 hover:brightness-95 transition-colors duration-200 h-[36px]"
                  >
                    <Image
                      src="https://ik.imagekit.io/mileseducation/miles_website/accounting/miles-education-cpa-fees-miles-education-cma/Call.webp"
                      alt="Call icon"
                      className="w-5 h-5"
                      width={40}
                      height={40}
                      priority
                    />
                    <span className="flex flex-col items-start pt-2">
                      <span className="text-xs font-normal leading-[0.5]">
                        Talk to an Expert
                      </span>
                      <span className="text-lg font-bold">95138 81313</span>
                    </span>
                  </a>
                )}

                {/* Login Button - Only show on CPA/CMA pages */}
                {showLoginButton && (
                  <Link
                    href={loginUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex gap-1 items-center justify-center text-[#3399FF] text-[14px] font-bold min-w-[70px] h-[24px] transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M18.75 3H11.25C10.6533 3 10.081 3.23705 9.65901 3.65901C9.23705 4.08097 9 4.65326 9 5.25V6C9 6.19891 9.07902 6.38968 9.21967 6.53033C9.36032 6.67098 9.55109 6.75 9.75 6.75C9.94891 6.75 10.1397 6.67098 10.2803 6.53033C10.421 6.38968 10.5 6.19891 10.5 6V5.25C10.5 5.05109 10.579 4.86032 10.7197 4.71967C10.8603 4.57902 11.0511 4.5 11.25 4.5H18.75C18.9489 4.5 19.1397 4.57902 19.2803 4.71967C19.421 4.86032 19.5 5.05109 19.5 5.25V18.75C19.5 18.9489 19.421 19.1397 19.2803 19.2803C19.1397 19.421 18.9489 19.5 18.75 19.5H11.25C11.0511 19.5 10.8603 19.421 10.7197 19.2803C10.579 19.1397 10.5 18.9489 10.5 18.75V18C10.5 17.8011 10.421 17.6103 10.2803 17.4697C10.1397 17.329 9.94891 17.25 9.75 17.25C9.55109 17.25 9.36032 17.329 9.21967 17.4697C9.07902 17.6103 9 17.8011 9 18V18.75C9 19.3467 9.23705 19.919 9.65901 20.341C10.081 20.7629 10.6533 21 11.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V5.25C21 4.65326 20.7629 4.08097 20.341 3.65901C19.919 3.23705 19.3467 3 18.75 3Z"
                        fill="#3399FF"
                      />
                      <path
                        d="M12.66 14.6533C12.5203 14.7938 12.4419 14.9839 12.4419 15.1821C12.4419 15.3802 12.5203 15.5703 12.66 15.7108C12.8005 15.8505 12.9906 15.9289 13.1887 15.9289C13.3869 15.9289 13.577 15.8505 13.7175 15.7108L15.84 13.5883C16.2614 13.1664 16.498 12.5946 16.498 11.9983C16.498 11.4021 16.2614 10.8302 15.84 10.4083L13.7175 8.28581C13.574 8.16294 13.3895 8.09874 13.2007 8.10603C13.012 8.11332 12.8329 8.19157 12.6993 8.32514C12.5658 8.45871 12.4875 8.63776 12.4802 8.82652C12.4729 9.01528 12.5371 9.19984 12.66 9.34331L14.5575 11.2483H3.75C3.55109 11.2483 3.36032 11.3273 3.21967 11.468C3.07902 11.6086 3 11.7994 3 11.9983C3 12.1972 3.07902 12.388 3.21967 12.5286C3.36032 12.6693 3.55109 12.7483 3.75 12.7483H14.5575L12.66 14.6533Z"
                        fill="#3399FF"
                      />
                    </svg>
                    Login
                  </Link>
                )}
              </div>

              <div className="flex lg:hidden items-center gap-2">
                {/* Download App Button - Mobile */}
                <button
                  type="button"
                  onClick={handleDownloadAppClick}
                  className="hidden md:inline-flex gap-1 items-center rounded-[13px_13px_13px_0] px-2 py-1 border-2 border-[#3399FF] text-[#39f] transition text-[13px] font-semibold shadow-md whitespace-nowrap h-[36px]"
                >
                  Download App
                </button>

                {/* Contact Us Button - Mobile (Tablet) - Hide for Miles Foundation */}
                {!isMilesFoundationPage && (
                  <a
                    href="tel:+919513881313"
                    aria-label="Call us for more details"
                    className="hidden md:flex bg-[#3399FF] text-white rounded-[20px_20px_20px_0] items-center justify-center font-bold text-base gap-2 px-2 py-[5px] pr-4 hover:brightness-95 transition-colors duration-200 h-[36px]"
                  >
                    {/* 40x40 was 2x the 20px it actually paints at, so the
                        browser fetched four times the pixels it needed. */}
                    <Image
                      src="https://ik.imagekit.io/mileseducation/miles_website/accounting/miles-education-cpa-fees-miles-education-cma/Call.webp"
                      alt="Call icon"
                      className="w-5 h-5"
                      width={20}
                      height={20}
                    />
                    <span className="flex flex-col items-start pt-2">
                      <span className="text-[10px] font-normal leading-[0.5]">
                        Talk to an Expert
                      </span>
                      <span className="text-base font-bold">95138 81313</span>
                    </span>
                  </a>
                )}

                {/* Login Button - Mobile - Only show on CPA/CMA pages */}
                {showLoginButton && (
                  <Link
                    href={loginUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden md:inline-flex gap-1 items-center justify-center text-[#3399FF] text-[14px] font-bold min-w-[70px] h-[24px] transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M18.75 3H11.25C10.6533 3 10.081 3.23705 9.65901 3.65901C9.23705 4.08097 9 4.65326 9 5.25V6C9 6.19891 9.07902 6.38968 9.21967 6.53033C9.36032 6.67098 9.55109 6.75 9.75 6.75C9.94891 6.75 10.1397 6.67098 10.2803 6.53033C10.421 6.38968 10.5 6.19891 10.5 6V5.25C10.5 5.05109 10.579 4.86032 10.7197 4.71967C10.8603 4.57902 11.0511 4.5 11.25 4.5H18.75C18.9489 4.5 19.1397 4.57902 19.2803 4.71967C19.421 4.86032 19.5 5.05109 19.5 5.25V18.75C19.5 18.9489 19.421 19.1397 19.2803 19.2803C19.1397 19.421 18.9489 19.5 18.75 19.5H11.25C11.0511 19.5 10.8603 19.421 10.7197 19.2803C10.579 19.1397 10.5 18.9489 10.5 18.75V18C10.5 17.8011 10.421 17.6103 10.2803 17.4697C10.1397 17.329 9.94891 17.25 9.75 17.25C9.55109 17.25 9.36032 17.329 9.21967 17.4697C9.07902 17.6103 9 17.8011 9 18V18.75C9 19.3467 9.23705 19.919 9.65901 20.341C10.081 20.7629 10.6533 21 11.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V5.25C21 4.65326 20.7629 4.08097 20.341 3.65901C19.919 3.23705 19.3467 3 18.75 3Z"
                        fill="#3399FF"
                      />
                      <path
                        d="M12.66 14.6533C12.5203 14.7938 12.4419 14.9839 12.4419 15.1821C12.4419 15.3802 12.5203 15.5703 12.66 15.7108C12.8005 15.8505 12.9906 15.9289 13.1887 15.9289C13.3869 15.9289 13.577 15.8505 13.7175 15.7108L15.84 13.5883C16.2614 13.1664 16.498 12.5946 16.498 11.9983C16.498 11.4021 16.2614 10.8302 15.84 10.4083L13.7175 8.28581C13.574 8.16294 13.3895 8.09874 13.2007 8.10603C13.012 8.11332 12.8329 8.19157 12.6993 8.32514C12.5658 8.45871 12.4875 8.63776 12.4802 8.82652C12.4729 9.01528 12.5371 9.19984 12.66 9.34331L14.5575 11.2483H3.75C3.55109 11.2483 3.36032 11.3273 3.21967 11.468C3.07902 11.6086 3 11.7994 3 11.9983C3 12.1972 3.07902 12.388 3.21967 12.5286C3.36032 12.6693 3.55109 12.7483 3.75 12.7483H14.5575L12.66 14.6533Z"
                        fill="#3399FF"
                      />
                    </svg>
                    Login
                  </Link>
                  // <Link
                  //   href={loginUrl}
                  //   target="_blank"
                  //   rel="noopener noreferrer"
                  //   className="hidden md:inline-flex gap-1 items-center rounded-[13px_13px_13px_0] px-2 py-1 bg-[#2F80ED] hover:bg-[#3274cf] transition text-white text-[13px] font-semibold shadow-md whitespace-nowrap"
                  // >
                  //   Login
                  // </Link>
                )}

                {/* Mobile Small Screen - Hide for Miles Foundation, show Call Now for others */}
                {!isMilesFoundationPage && <CallNowButtonVisit />}

                <button
                  className="lg:hidden p-2 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40 transition-colors"
                  onClick={openMobile}
                  aria-label="Open menu"
                  aria-haspopup="dialog"
                  aria-controls="global-mobile-menu"
                  type="button"
                >
                  <FiMenu size={22} />
                </button>
              </div>
            </div>
            {secondaryItems.length > 0 && (
              <div
                className="hidden lg:block absolute left-0 right-0 top-20 xl:top-[85px] z-[49]"
                aria-label="Section navigation"
              >
                <div className="mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="bg-[#191818bf] backdrop-blur rounded-full px-6 py-2 shadow-md border border-white/10">
                    <nav className="flex justify-between items-center gap-6">
                      {secondaryItems?.map((it, i) => (
                        <Link
                          key={i}
                          href={it.url || "#"}
                          className={classNames(
                            "text-xs xl:text-[11px] font-bold whitespace-nowrap",
                            activeChain.includes(it)
                              ? "bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] bg-clip-text text-transparent font-bold"
                              : matchesPath(it.url, pathname || "/")
                                ? "text-[#F2B233]"
                                : "text-white",
                            "hover:bg-[linear-gradient(95deg,#f5b644_11.82%,#ffeed3_99.75%)] hover:bg-clip-text hover:text-transparent",
                          )}
                        >
                          {it.title}
                        </Link>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showMobileMenu && (
          <div
            className="fixed inset-0 z-50 lg:hidden pointer-events-auto"
            role="dialog"
            aria-modal="true"
            id="global-mobile-menu"
          >
            <div
              onClick={closeMobile}
              className={classNames(
                "absolute inset-0 bg-black/50 transition-opacity duration-300 ease-out",
                mobileOpen ? "opacity-100" : "opacity-0",
              )}
            />
            <aside
              className={classNames(
                "absolute right-0 top-0 h-full w-[90%] max-w-[420px] bg-[#111] text-white shadow-2xl",
                "transition-all duration-300 outline-none transform",
                mobileOpen
                  ? "translate-x-0 scale-100"
                  : "translate-x-full scale-95",
              )}
              tabIndex={-1}
            >
              <div
                className={classNames(
                  "flex items-center justify-end px-4 h-16 border-b border-white/10 transition-opacity duration-300",
                  mobileOpen ? "opacity-100" : "opacity-0",
                )}
                style={{
                  transitionDelay: mobileOpen ? "100ms" : "0ms",
                }}
              >
                <button
                  aria-label="Close menu"
                  className="p-2 rounded-lg border border-white/10 hover:bg-white/10 bg-white/5 focus:outline-none focus:ring-2 focus:ring-white/40 transition-all duration-200"
                  onClick={closeMobile}
                  type="button"
                >
                  <FiX size={22} />
                </button>
              </div>

              <nav
                className={classNames(
                  "overflow-y-auto h-[calc(100%-4rem)] pb-10 transition-opacity duration-300",
                  mobileOpen ? "opacity-100" : "opacity-0",
                )}
                style={{
                  transitionDelay: mobileOpen ? "150ms" : "0ms",
                }}
              >
                {/* Login Button - New Design for CPA/CMA pages */}
                {showLoginButton && (
                  <div className="px-4 pt-4 pb-6">
                    <Link
                      href={loginUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center py-1 px-6 rounded-full border border-[#39F] text-white text-base font-semibold hover:bg-[#2A85FF]/10 transition-all duration-200 relative z-10"
                    >
                      Log in
                    </Link>
                  </div>
                )}

                {mobileMenuItems
                  ?.filter((node) => {
                    // Filter out Login and Contact Us from menu items since we're showing them separately
                    if (node.title === "Login" || node.title === "Contact Us") {
                      return false;
                    }
                    return true;
                  })
                  .map((node, i) => {
                    return (
                      <div
                        key={i}
                        className={classNames(
                          "transition-all duration-300 ease-out",
                          // A childless top-level row (About) draws no divider
                          // of its own: inside MobileAccordion it is the only
                          // child, so both of that component's `border-b`s are
                          // cancelled by their `last:border-0`. The rows with
                          // an accordion get theirs from the toggle row.
                          !getChildren(node)?.length &&
                            "border-b border-white/10",
                          mobileOpen
                            ? "opacity-100 translate-x-0"
                            : "opacity-0 translate-x-4",
                        )}
                        style={{
                          transitionDelay: mobileOpen
                            ? `${200 + (showLoginButton ? i + 1 : i) * 50}ms`
                            : "0ms",
                        }}
                      >
                        <MobileAccordion
                          node={node}
                          depth={0}
                          activeChain={mobileActiveChain}
                          pathname={pathname || "/"}
                          idPath={(node.url || node.title || "root") + "@0"}
                          onDownloadAppClick={() => {
                            setShowDownloadModal(true);
                            closeMobile();
                          }}
                        />
                      </div>
                    );
                  })}

                {/* Additional Menu Items: Download App, Contact Us, Careers */}
                <div className="border-b border-white/10 last:border-0">
                  {/* 1. Download App */}
                  <div className="flex items-center justify-between py-2 px-4 text-sm border-b border-white/10 last:border-0 pl-3">
                    <button
                      type="button"
                      onClick={() => {
                        handleDownloadAppClick();
                        closeMobile();
                      }}
                      className="flex-1 text-left focus:outline-none rounded transition-colors text-white"
                    >
                      Download App
                    </button>
                  </div>
                  {/* 2. Contact Us */}
                  <div className="flex items-center justify-between py-2 px-4 text-sm border-b border-white/10 last:border-0 pl-3">
                    <Link
                      href="/contact-us"
                      className="flex-1 text-left focus:outline-none rounded transition-colors text-white"
                      onClick={closeMobile}
                    >
                      Contact Us
                    </Link>
                  </div>
                  {/* 3. Careers */}
                  <div className="flex items-center justify-between py-2 px-4 text-sm border-b border-white/10 last:border-0 pl-3">
                    <Link
                      href="https://mileseducation.zohorecruit.com/jobs/Careers"
                      className="flex-1 text-left focus:outline-none rounded transition-colors text-white"
                      onClick={closeMobile}
                    >
                      Careers
                    </Link>
                  </div>
                </div>
              </nav>
            </aside>
          </div>
        )}
      </header>
      <div ref={spacerRef} aria-hidden="true" />
      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-[auto] mx-auto border border-white p-4 w-[400px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              type="button"
              onClick={() => setShowDownloadModal(false)}
              className="text-white text-xl md:text-2xl absolute right-1 top-1 cursor-pointer"
            >
              ✕
            </button>
            <div className="text-center pt-4">
              <h3 className="text-white text-2xl font-semibold mb-2">
                Get Miles One App
              </h3>
              <p className="text-[#fff] text-sm mb-4">
                The #1 App for AI-Ready Accountants
              </p>
              <DownloadApp />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalHeaderUpdated;
